import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    Download, Upload, Copy, ClipboardPaste, X,
} from "lucide-react";
import api from "../api";
import { buildShiftColorMap } from "../utils/shiftColors";

const HRbaseurl = process.env.REACT_APP_BACKEND_HR_BASE_URL;
const OFF_BRUSH = "OFF";
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Date helpers ─────────────────────────────────────────────────────────────

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (v) => { const [y, m, d] = String(v).split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

const shiftHours = (shift) => {
    if (!shift || !shift.start_time || !shift.end_time) return 0;
    const parse = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    let s = parse(shift.start_time), e = parse(shift.end_time);
    if (e < s) e += 24 * 60;
    return (e - s) / 60;
};

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`display:flex; flex-direction:column; gap:16px; width:100%; max-width:100%; min-width:0; box-sizing:border-box; overflow:hidden;`;

const TopActions = styled.div`display:flex; gap:10px; align-items:center; flex-wrap:wrap;`;

const SavedPill = styled.div`
  display:flex; align-items:center; gap:8px; padding:8px 14px; border-radius:var(--radius-xs);
  background:var(--bg3); border:1px solid var(--border-light); font-size:13px; color:var(--text-muted); white-space:nowrap;
`;
const Dot = styled.span`width:7px; height:7px; border-radius:50%; background:${(p) => (p.$dirty ? "var(--warning)" : "var(--success)")};`;

const GhostBtn = styled.button`
  display:flex; align-items:center; gap:7px; padding:9px 16px; border-radius:var(--radius-xs);
  border:1px solid var(--border); background:var(--bg3); color:var(--text-muted);
  font:600 13px inherit; cursor:pointer; text-decoration:none; white-space:nowrap;
  &:hover { background: var(--glass); color: var(--text); }
`;
const PrimaryBtn = styled.button`
  padding:9px 18px; border-radius:var(--radius-xs); border:none; background:var(--primary); color:#fff;
  font:600 13px inherit; cursor:pointer; white-space:nowrap;
  &:hover { background: var(--primary-dark); }
  &:disabled { opacity:.45; cursor:not-allowed; }
`;

const ContainerCard = styled.div`
  display: flex; flex-direction: column; gap: 14px; padding: 16px;
  border-radius: var(--radius-sm); background: var(--bg2); border: 1px solid var(--border-light);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15); width: 100%; max-width: 100%; min-width: 0; box-sizing: border-box; overflow: hidden;
`;

const ControlBar = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 10px 14px;
  border-radius: var(--radius-sm); background: linear-gradient(180deg, rgba(102,126,234,0.10), var(--bg1));
  border: 1px solid var(--border-light);
`;

const BrushSelectWrap = styled.div`
  display: flex; align-items: center; gap: 8px;
`;
const BrushLabel = styled.span`
  font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--text-muted);
`;
const BrushSelect = styled.select`
  padding: 6px 12px; border-radius: 8px; border: 1.5px solid ${(p) => p.$color ? p.$color.border : "var(--border-light)"};
  background: ${(p) => p.$color ? p.$color.bg : "var(--bg1)"};
  color: ${(p) => p.$color ? p.$color.text : "var(--text)"};
  font: 700 12.5px inherit; outline: none; cursor: pointer; transition: all 0.15s ease;
  option { background: var(--bg2); color: var(--text); font-weight: 600; }
`;
const BrushBadge = styled.div`
  display: flex; align-items: center; justify-content: center; padding: 5px 10px; border-radius: 7px;
  font-size: 11.5px; font-weight: 700; background: ${(p) => p.$color.bg}; color: ${(p) => p.$color.text};
  border: 1px solid ${(p) => p.$color.border}; white-space: nowrap;
`;

const TabGroup = styled.div`
  display: flex; gap: 4px; padding: 3px; background: var(--bg1);
  border-radius: var(--radius-xs); border: 1px solid var(--border-light);
`;
const TabBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 7px;
  font-size: 12.5px; font-weight: 700; border: none; cursor: pointer; white-space: nowrap; transition: all 0.15s ease;
  background: ${(p) => (p.$active ? "var(--primary)" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "var(--text-muted)")};
  &:hover { color: ${(p) => (p.$active ? "#fff" : "var(--text)")}; }
`;

const BulkTabBar = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 12px 14px;
  border-radius: var(--radius-sm); background: linear-gradient(180deg, rgba(102,126,234,0.14), var(--bg1));
  border: 1px solid rgba(102,126,234,0.35); animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
const BulkTabActionsGroup = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 1;
`;
const BulkTabHint = styled.div`
  font-size: 12.5px; color: var(--text-dim); flex: 1;
`;



const SummaryStrip = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 16px;
  border-radius: var(--radius-sm); background: var(--bg1); border: 1px solid var(--border-light); flex-wrap: wrap;
`;
const StatItem = styled.div`
  display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--text-muted);
  span { color: var(--text); font-weight: 700; }
`;
const SummaryDot = styled.span`width: 8px; height: 8px; border-radius: 50%; background: ${(p) => p.$color || "var(--primary)"};`;
const StatusPill = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;
  background: ${(p) => (p.$clear ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)")};
  color: ${(p) => (p.$clear ? "#34d399" : "#fbbf24")};
  border: 1px solid ${(p) => (p.$clear ? "rgba(16, 185, 129, 0.3)" : "rgba(245, 158, 11, 0.3)")};
  cursor: pointer;
`;

const BulkBadge = styled.div`
  display: flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px;
  background: rgba(99, 102, 241, 0.25); border: 1px solid rgba(99, 102, 241, 0.45); color: #a5b4fc; font-size: 13px; font-weight: 700; white-space: nowrap;
`;
const BulkActionButton = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 7px 13px; border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12); background: rgba(255, 255, 255, 0.06); color: #f1f5f9; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: all 0.15s ease;
  &:hover { background: rgba(255, 255, 255, 0.14); border-color: rgba(255, 255, 255, 0.25); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const BulkDangerButton = styled(BulkActionButton)`
  background: rgba(239, 68, 68, 0.15); border-color: rgba(239, 68, 68, 0.35); color: #fca5a5;
  &:hover { background: rgba(239, 68, 68, 0.28); border-color: rgba(239, 68, 68, 0.5); }
`;
const BulkCloseButton = styled.button`
  display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 50%;
  border: none; background: rgba(255, 255, 255, 0.1); color: #94a3b8; cursor: pointer; margin-left: 4px;
  &:hover { background: rgba(255, 255, 255, 0.2); color: #fff; }
`;


const FilterBar = styled.div`
  display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:12px 14px; border-radius:var(--radius-sm);
  background:var(--bg2); border:1px solid var(--border-light);
`;
const RangeGroup = styled.div`display:flex; align-items:center; gap:2px; padding:3px; background:var(--bg1); border-radius:var(--radius-xs); border:1px solid var(--border-light);`;
const RangeArrow = styled.div`
  width:26px; height:26px; display:flex; align-items:center; justify-content:center; border-radius:7px;
  color:var(--text-muted); cursor:pointer; font-size:16px; user-select:none;
  &:hover { background: var(--glass); }
`;
const DateField = styled.div`display:flex; flex-direction:column; gap:2px; padding:0 6px;`;
const DateFieldLabel = styled.span`font-size:9.5px; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:var(--text-dim);`;
const DateInput = styled.input`
  padding:4px 7px; border-radius:7px; border:1px solid var(--border-light); background:var(--bg1); color:var(--text);
  font:600 12.5px inherit; outline:none; color-scheme:dark; cursor:pointer;
`;
const PresetGroup = styled.div`display:flex; gap:3px; padding:3px; background:var(--bg1); border-radius:var(--radius-xs); border:1px solid var(--border-light);`;
const PresetBtn = styled.div`
  padding:5px 13px; border-radius:7px; font-size:12.5px; font-weight:600; cursor:pointer; white-space:nowrap;
  background: ${(p) => (p.$active ? "var(--primary)" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "var(--text-muted)")};
`;
const RangeSummary = styled.div`font-size:12px; color:var(--text-dim); white-space:nowrap;`;
const SearchWrap = styled.div`position:relative; display:flex; align-items:center;`;
const SearchInput = styled.input`
  padding:8px 12px; width:190px; border-radius:9px; border:1px solid var(--border-light); background:var(--bg1);
  color:var(--text); font:400 13px inherit; outline:none;
`;
const DeptSelect = styled.select`
  padding:8px 12px; border-radius:9px; border:1px solid var(--border-light); background:var(--bg1); color:var(--text-muted);
  font:500 13px inherit; outline:none; cursor:pointer;
  option { background: var(--bg2); color: var(--text); }
`;

const ClipChip = styled.div`
  display:${(p) => (p.$show ? "flex" : "none")}; align-items:center; gap:7px; padding:6px 11px; border-radius:9px;
  background:rgba(16,185,129,.12); border:1px solid rgba(16,185,129,.4); color:var(--success); font-size:12px; font-weight:600;
`;

const CardHeaderBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding-bottom: 8px; border-bottom: 1px solid var(--border-light);
`;

const GridCard = styled.div`border-radius:var(--radius-sm); background:var(--bg2); border:1px solid var(--border-light); overflow:hidden; width:100%; max-width:100%; min-width:0; box-sizing:border-box;`;
const GridScroll = styled.div`
  overflow-x: auto; overflow-y: auto; max-height: 60vh; width: 100%; max-width: 100%; min-width: 0; box-sizing: border-box;

  /* Custom Inline Scrollbar */
  &::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.6);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.45);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.75);
  }
`;
const GridBody = styled.div`display:grid; width:max-content; min-width:100%;`;

const HeaderNameCell = styled.div`
  position:sticky; left:0; top:0; z-index:6; background:var(--bg3);
  border-right:1px solid var(--border-light); border-bottom:1px solid var(--border-light);
  padding:10px 14px; display:flex; align-items:center; gap:9px;
`;
const HeaderDayCell = styled.div`
  position:sticky; top:0; z-index:4;
  background: ${(p) => (p.$weekend ? "rgba(239,68,68,.10)" : "var(--bg3)")};
  border-bottom: ${(p) => (p.$weekend ? "2px solid var(--danger)" : "1px solid var(--border-light)")};
  text-align:center; padding:7px 4px; cursor:pointer; user-select:none;
`;
const DowLabel = styled.div`font-size:10.5px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:${(p) => (p.$weekend ? "#fca5a5" : "var(--text-muted)")};`;
const DayNum = styled.div`font-size:15px; font-weight:700; color:${(p) => (p.$weekend ? "#fca5a5" : "var(--text)")}; line-height:1.2;`;

const NameCell = styled.div`
  position:sticky; left:0; z-index:2;
  background: ${(p) => (p.$selected ? "rgba(102,126,234,.14)" : "var(--bg2)")};
  border-right:1px solid var(--border-light); border-bottom:1px solid var(--border-light);
  padding:8px 12px; display:flex; align-items:center; gap:9px;
`;
const NameInfo = styled.div`min-width:0; flex:1;`;
const NameText = styled.div`font-size:13.5px; font-weight:600; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;`;
const DeptText = styled.div`font-size:11px; color:var(--text-dim); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;`;
const HoursPill = styled.div`
  flex:none; font-size:11px; font-weight:700; padding:3px 7px; border-radius:6px; white-space:nowrap;
  background: ${(p) => (p.$over ? "rgba(239,68,68,.15)" : "var(--glass-dark)")};
  color: ${(p) => (p.$over ? "#ff8ba6" : "var(--text-muted)")};
`;

const DayCell = styled.div`
  border-bottom:1px solid var(--border-light);
  background: ${(p) => (p.$weekend ? "rgba(239,68,68,.06)" : "transparent")};
  padding:5px; display:flex; align-items:center; justify-content:center; cursor:pointer; user-select:none;
  opacity: ${(p) => (p.$disallowed ? 0.35 : 1)};
`;
const CellLabel = styled.span`
  display:flex; align-items:center; justify-content:center; width:100%; padding:6px 2px; border-radius:8px;
  font-size:12px; font-weight:700;
  background: ${(p) => p.$color.bg}; color: ${(p) => p.$color.text}; border:1px solid ${(p) => p.$color.border};
`;

const FooterNameCell = styled.div`
  position:sticky; left:0; z-index:2; background:var(--bg3); border-right:1px solid var(--border-light);
  border-top:1px solid var(--border-light); padding:10px 14px; display:flex; align-items:center;
  font-size:11px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--text-muted);
`;
const CoverageCell = styled.div`
  border-top:1px solid var(--border-light); padding:10px 6px; display:flex; flex-direction:column; align-items:center; gap:5px;
`;
const CoverageCount = styled.div`font-size:14px; font-weight:700; color:${(p) => (p.$low ? "var(--warning)" : "var(--success)")};`;
const CoverageBarBg = styled.div`height:3px; width:70%; border-radius:2px; background:var(--glass); overflow:hidden;`;
const CoverageBarFill = styled.div`height:100%; background:${(p) => (p.$low ? "var(--warning)" : "var(--success)")};`;

const HintRow = styled.div`font-size:12px; color:var(--text-dim); display:flex; gap:18px; flex-wrap:wrap;`;



// ─── Import review modal ──────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position:fixed; inset:0; z-index:2000; background:rgba(4,6,12,.72); backdrop-filter:blur(4px);
  display:flex; align-items:center; justify-content:center; padding:24px;
`;
const ModalContent = styled.div`
  width:min(960px,100%); max-height:88vh; display:flex; flex-direction:column; background:var(--bg2);
  border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow); overflow:hidden;
`;
const ModalHeader = styled.div`padding:18px 22px; border-bottom:1px solid var(--border-light); display:flex; align-items:flex-start; justify-content:space-between; gap:16px;`;
const ModalStatsBar = styled.div`padding:12px 22px; display:flex; gap:8px; flex-wrap:wrap; align-items:center; border-bottom:1px solid var(--border-light); background:var(--bg1);`;
const StatPill = styled.div`display:flex; align-items:center; gap:7px; padding:5px 11px; border-radius:8px; background:var(--bg2); border:1px solid var(--border-light); font-size:12px; font-weight:600; color:var(--text);`;
const ModalBody = styled.div`overflow:auto; flex:1;`;
const ImportGrid = styled.div`display:grid; grid-template-columns:44px 200px 130px 1fr; align-items:stretch;`;
const ImportHead = styled.div`padding:9px 14px; font:700 11px inherit; letter-spacing:.08em; text-transform:uppercase; color:var(--text-muted); background:var(--bg3); border-bottom:1px solid var(--border-light); position:sticky; top:0;`;
const ImportCell = styled.div`padding:11px 14px; border-bottom:1px solid var(--border-light); display:flex; flex-direction:column; justify-content:center; gap:2px; background:${(p) => (p.$flag ? "rgba(239,68,68,.05)" : "transparent")};`;
const StatusBadge = styled.span`display:inline-flex; align-items:center; padding:3px 9px; border-radius:20px; font-size:11px; font-weight:700; background:${(p) => p.$bg}; color:${(p) => p.$fg}; width:fit-content;`;
const CellSelect = styled.select`
  padding:3px 6px; border-radius:6px; font-size:11px; font-weight:700; max-width:150px; cursor:pointer;
  background:${(p) => p.$bg}; color:${(p) => p.$fg}; border:1px ${(p) => (p.$dashed ? "dashed" : "solid")} ${(p) => p.$border};
`;
const ModalFooter = styled.div`padding:16px 22px; border-top:1px solid var(--border-light); display:flex; align-items:center; gap:12px; background:var(--bg1);`;

const SKIP_FIX = "__SKIP__";
const OFF_FIX = "__OFF__";

const ImportReviewModal = ({ data, shifts, departments, cellValue, onClose, onApply, isUploading }) => {
    const departmentShiftNames = useMemo(() => {
        const m = new Map();
        departments.forEach((d) => m.set(d.name, d.shifts.map((s) => s.name)));
        return m;
    }, [departments]);
    const allShiftNames = useMemo(() => shifts.map((s) => s.name), [shifts]);

    const rows = useMemo(() => (data.preview || []).map((emp) => {
        const unknownPerson = !emp.id;
        const diff = [];
        Object.entries(emp.shifts || {}).forEach(([dateStr, info]) => {
            if (!info) return;
            const incomingName = (info.name || "").trim();
            const currentShiftId = emp.id ? cellValue(emp.id, dateStr) : null;
            const currentShift = currentShiftId != null ? shifts.find((s) => s.id === currentShiftId) : null;
            const currentName = currentShift ? currentShift.name.toUpperCase() : "OFF";
            if (!info.is_valid) {
                diff.push({ dateStr, bad: true, rawText: incomingName || "(blank)", currentName });
                return;
            }
            // Resolve to the shift's actual stored casing so it matches a real <option value> exactly.
            const matchedShift = incomingName === "" ? null : shifts.find((s) => s.name.toUpperCase() === incomingName.toUpperCase());
            const incomingValue = incomingName === "" || incomingName.toUpperCase() === "OFF" ? "" : (matchedShift ? matchedShift.name : incomingName);
            diff.push({ dateStr, bad: false, incomingValue, currentName });
        });
        const deptOptions = Array.from(new Set(departmentShiftNames.get(emp.department) || allShiftNames));
        return {
            id: emp.id, name: emp.name, department: emp.department,
            unknownPerson, diff, deptOptions,
            locked: unknownPerson,
            note: unknownPerson ? "No matching employee in this roster — row skipped." : "",
        };
    }), [data.preview, cellValue, shifts, departmentShiftNames, allShiftNames]);

    const [accepted, setAccepted] = useState(() => new Set(rows.map((r, i) => i).filter((i) => !rows[i].locked)));
    const [fixes, setFixes] = useState({});

    const setFix = (rowIdx, dateStr, value) => setFixes((prev) => ({ ...prev, [`${rowIdx}|${dateStr}`]: value }));

    // Every cell is editable: default selection is the incoming value (or Skip for an invalid code),
    // restricted to that employee's department's own shifts + Off + Skip. Picking a value overrides it.
    const rowsView = useMemo(() => rows.map((r, i) => {
        let changedCount = 0;
        let skipCount = 0;
        const diff = r.diff.map((d) => {
            const override = fixes[`${i}|${d.dateStr}`];
            const selectValue = override !== undefined ? override : (d.bad ? SKIP_FIX : (d.incomingValue === "" ? OFF_FIX : d.incomingValue));
            const skip = selectValue === SKIP_FIX;
            const nameValue = skip ? null : (selectValue === OFF_FIX ? "" : selectValue);
            const changed = !skip && (nameValue === "" ? "OFF" : nameValue) !== d.currentName;
            if (skip) skipCount += 1;
            else if (changed) changedCount += 1;
            return { ...d, selectValue, skip, nameValue, changed };
        });
        let status = "Unchanged";
        if (r.unknownPerson) status = "Not found";
        else if (skipCount) status = `${skipCount} skipped`;
        else if (changedCount) status = `${changedCount} changed`;
        return { ...r, diff, changedCount, skipCount, status };
    }), [rows, fixes]);

    const toggleRow = (i) => setAccepted((prev) => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i); else next.add(i);
        return next;
    });

    const eligibleIdxs = rowsView.map((r, i) => i).filter((i) => !rowsView[i].locked);
    const allSelected = eligibleIdxs.length > 0 && eligibleIdxs.every((i) => accepted.has(i));
    const toggleAll = () => setAccepted(allSelected ? new Set() : new Set(eligibleIdxs));

    const notFoundCount = rowsView.filter((r) => r.unknownPerson).length;
    const skippedRowCount = rowsView.filter((r) => !r.unknownPerson && r.skipCount > 0).length;
    const changedRowCount = rowsView.filter((r) => !r.unknownPerson && !r.skipCount && r.changedCount > 0).length;
    const unchangedRowCount = rowsView.filter((r) => !r.unknownPerson && !r.skipCount && r.changedCount === 0).length;
    const acceptedCount = accepted.size;
    const skipped = rowsView.length - acceptedCount;
    const skippedCells = rowsView.reduce((t, r, i) => t + (accepted.has(i) && !r.locked ? r.skipCount : 0), 0);

    const handleApply = () => {
        const acceptedRows = [];
        rowsView.forEach((r, i) => {
            if (!accepted.has(i) || r.locked) return;
            const finalShifts = {};
            r.diff.forEach((d) => {
                if (d.skip) return;
                finalShifts[d.dateStr] = { name: d.nameValue, is_valid: true };
            });
            if (Object.keys(finalShifts).length) acceptedRows.push({ id: r.id, name: r.name, department: r.department, shifts: finalShifts });
        });
        onApply(acceptedRows);
    };

    const emptyImport = rowsView.length === 0;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <div>
                        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4, color: "var(--text)" }}>Review before importing</div>
                        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                            {emptyImport ? "No rows could be read from this file" : `${rows.length} row${rows.length === 1 ? "" : "s"} read · nothing is changed until you confirm`}
                        </div>
                    </div>
                    <div style={{ fontSize: 20, color: "var(--text-muted)", cursor: "pointer", lineHeight: 1, padding: "2px 6px" }} onClick={onClose}><X size={20} /></div>
                </ModalHeader>

                {!emptyImport && (
                    <ModalStatsBar>
                        <StatPill><SummaryDot $color="var(--primary-light)" style={{ marginRight: 4 }} />{changedRowCount} changed</StatPill>
                        <StatPill><SummaryDot $color="var(--text-dim)" style={{ marginRight: 4 }} />{unchangedRowCount} unchanged</StatPill>
                        <StatPill><SummaryDot $color="var(--warning)" style={{ marginRight: 4 }} />{skippedRowCount} with skipped cells</StatPill>
                        <StatPill><SummaryDot $color="var(--danger)" style={{ marginRight: 4 }} />{notFoundCount} not found</StatPill>
                        <div style={{ flex: 1 }} />
                        <GhostBtn onClick={toggleAll}>{allSelected ? "Deselect all" : "Select all valid rows"}</GhostBtn>
                    </ModalStatsBar>
                )}

                <ModalBody>
                    {emptyImport ? (
                        <div style={{ padding: "44px 30px", textAlign: "center" }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--warning)", marginBottom: 8 }}>Couldn&rsquo;t read any rows from this file</div>
                            <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--text-muted)", maxWidth: 420, margin: "0 auto" }}>
                                Supported formats are .xls, .csv and .tsv, with columns Employee ID, Employee Name, Department, then one column per date.
                            </div>
                        </div>
                    ) : (
                        <ImportGrid>
                            <ImportHead>&nbsp;</ImportHead>
                            <ImportHead>Employee</ImportHead>
                            <ImportHead>Status</ImportHead>
                            <ImportHead>Current &rarr; Incoming</ImportHead>
                            {rowsView.map((r, i) => {
                                const flag = r.unknownPerson || r.skipCount > 0;
                                const badgeColor = r.unknownPerson
                                    ? { bg: "rgba(239,68,68,.15)", fg: "#ff8ba6" }
                                    : r.skipCount ? { bg: "rgba(245,158,11,.15)", fg: "var(--warning)" }
                                        : r.changedCount ? { bg: "rgba(102,126,234,.18)", fg: "var(--primary-light)" }
                                            : { bg: "var(--glass-dark)", fg: "var(--text-muted)" };
                                return (
                                    <React.Fragment key={i}>
                                        <ImportCell $flag={flag}>
                                            <input type="checkbox" checked={accepted.has(i)} disabled={r.locked}
                                                onChange={() => toggleRow(i)}
                                                style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: r.locked ? "not-allowed" : "pointer" }} />
                                        </ImportCell>
                                        <ImportCell $flag={flag}>
                                            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{r.name}</div>
                                            <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.department}</div>
                                        </ImportCell>
                                        <ImportCell $flag={flag}>
                                            <StatusBadge $bg={badgeColor.bg} $fg={badgeColor.fg}>{r.status}</StatusBadge>
                                        </ImportCell>
                                        <ImportCell $flag={flag}>
                                            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                                                {r.diff.map((d, di) => {
                                                    const tone = d.skip
                                                        ? { bg: "rgba(245,158,11,.12)", fg: "var(--warning)", border: "rgba(245,158,11,.55)", dashed: true }
                                                        : d.changed
                                                            ? { bg: "rgba(102,126,234,.15)", fg: "var(--primary-light)", border: "rgba(102,126,234,.4)", dashed: false }
                                                            : { bg: "var(--glass-dark)", fg: "var(--text-dim)", border: "var(--border-light)", dashed: false };
                                                    return (
                                                        <CellSelect
                                                            key={di}
                                                            $bg={tone.bg} $fg={tone.fg} $border={tone.border} $dashed={tone.dashed}
                                                            title={d.bad ? `${d.dateStr}: incoming "${d.rawText}" isn't a valid shift for ${r.department}` : d.dateStr}
                                                            value={d.selectValue}
                                                            onChange={(e) => setFix(i, d.dateStr, e.target.value)}
                                                        >
                                                            {d.bad && <option value={SKIP_FIX}>Skip ({d.rawText})</option>}
                                                            {!d.bad && <option value={SKIP_FIX}>Skip</option>}
                                                            <option value={OFF_FIX}>UA</option>
                                                            {r.deptOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                                                        </CellSelect>
                                                    );
                                                })}
                                            </div>
                                            {r.note && <div style={{ fontSize: 11.5, color: "var(--warning)", marginTop: 5 }}>{r.note}</div>}
                                        </ImportCell>
                                    </React.Fragment>
                                );
                            })}
                        </ImportGrid>
                    )}
                </ModalBody>

                <ModalFooter>
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                        {skipped ? `${skipped} row${skipped === 1 ? "" : "s"} will be skipped` : "Only ticked rows are written to the roster"}
                        {skippedCells ? ` · ${skippedCells} unresolved cell${skippedCells === 1 ? "" : "s"} will be skipped` : ""}
                    </div>
                    <div style={{ flex: 1 }} />
                    <GhostBtn onClick={onClose}>Cancel</GhostBtn>
                    <PrimaryBtn disabled={isUploading || acceptedCount === 0} onClick={handleApply}>
                        {isUploading ? "Importing…" : acceptedCount ? `Import ${acceptedCount} row${acceptedCount === 1 ? "" : "s"}` : "Nothing selected"}
                    </PrimaryBtn>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DutyRoster = ({ shifts, departments, globalDepartments, role, isRestricted, userDept }) => {
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        return toISO(new Date(d.getFullYear(), d.getMonth(), 1));
    });
    const [toDate, setToDate] = useState(() => {
        const d = new Date();
        return toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0));
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDepts, setSelectedDepts] = useState(isRestricted && userDept ? userDept.split(",").filter(Boolean) : ["All"]);
    const [employees, setEmployees] = useState([]);
    const [rosterData, setRosterData] = useState([]);
    const [draftEdits, setDraftEdits] = useState({});
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [brush, setBrush] = useState(null);
    const [clipboard, setClipboard] = useState(null);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [activeTab, setActiveTab] = useState("view");
    const paintingRef = useRef(false);

    useEffect(() => {
        if (selectedEmployees.length > 0) {
            setActiveTab("bulk");
        }
    }, [selectedEmployees.length]);

    useEffect(() => {
        const stop = () => { paintingRef.current = false; };
        window.addEventListener("mouseup", stop);
        return () => window.removeEventListener("mouseup", stop);
    }, []);

    const shiftColorMap = useMemo(() => buildShiftColorMap(shifts), [shifts]);
    const activeShifts = useMemo(() => shifts.filter((s) => s.is_active), [shifts]);

    const brushValue = brush === OFF_BRUSH ? null : brush;

    const fetchRosterData = useCallback(async () => {
        try {
            const empParams = new URLSearchParams();
            const rosterParams = new URLSearchParams({ from_date: fromDate, to_date: toDate });
            if (isRestricted) {
                empParams.set("department", userDept);
                rosterParams.set("department", userDept);
            } else if (!selectedDepts.includes("All") && selectedDepts.length > 0) {
                const deptsStr = selectedDepts.join(",");
                empParams.set("department", deptsStr);
                rosterParams.set("department", deptsStr);
            }
            const empQS = empParams.toString();
            const [empRes, rosterRes] = await Promise.all([
                api.get(`employees_from_global/${empQS ? `?${empQS}` : ""}`),
                api.get(`roster/?${rosterParams.toString()}`),
            ]);
            setEmployees(
                empRes.data
                    .filter((e) => e.is_active)
                    .map((e) => ({
                        id: e.employeeId,
                        name: e.employeeName || e.name || e.employeeId,
                        department: e.department || "Unassigned",
                        department_id: e.department_id,
                    }))
            );
            setRosterData(rosterRes.data);
        } catch (error) {
            console.error("Error fetching roster data", error);
            toast.error("Failed to load roster data");
        }
    }, [fromDate, toDate, selectedDepts, isRestricted, userDept]);

    useEffect(() => { fetchRosterData(); }, [fetchRosterData]);

    const availableDepts = useMemo(() => globalDepartments.map((gd) => ({
        id: (gd.id || gd.department_code || "").toString(),
        name: gd.department_name || gd.name,
    })), [globalDepartments]);

    const uniqueDepartments = useMemo(() => {
        if (isRestricted) return availableDepts.filter((d) => userDept.split(",").includes(d.id));
        return [{ id: "All", name: "All" }, ...availableDepts];
    }, [isRestricted, availableDepts, userDept]);

    const filteredEmployees = useMemo(() => {
        const selectedIds = new Set(selectedDepts.filter((d) => d !== "All").map((d) => d.toString()));
        const selectedNames = new Set(availableDepts.filter((d) => selectedIds.has(d.id)).map((d) => d.name));
        const isAllDepts = selectedDepts.includes("All") || selectedDepts.length === 0;
        return employees.filter((e) => {
            const eid = e.id?.toString().toLowerCase() || "";
            const ename = e.name?.toLowerCase() || "";
            if (searchTerm && !eid.includes(searchTerm.toLowerCase()) && !ename.includes(searchTerm.toLowerCase())) return false;
            if (isAllDepts) return true;
            const deid = e.department_id?.toString();
            const dename = e.department?.toString();
            return (deid && selectedIds.has(deid)) || (dename && selectedNames.has(dename));
        });
    }, [employees, selectedDepts, availableDepts, searchTerm]);

    const employeesById = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
    const deptShiftIds = useMemo(() => {
        const m = new Map();
        departments.forEach((d) => m.set(d.name, d.shifts.map((s) => s.id)));
        return m;
    }, [departments]);

    const isAllowedShift = useCallback((empId, shiftId) => {
        if (shiftId === null) return true;
        const emp = employeesById.get(empId);
        const allowed = emp ? deptShiftIds.get(emp.department) : undefined;
        return !!(allowed && allowed.includes(shiftId));
    }, [employeesById, deptShiftIds]);

    // Shift brush narrows to the selected department's allowed shifts; "All" shows every active shift.
    const brushShifts = useMemo(() => {
        if (selectedDepts.includes("All") || selectedDepts.length === 0) return activeShifts;
        const ids = new Set(selectedDepts.map((d) => d.toString()));
        const deptNames = new Set(availableDepts.filter((d) => ids.has(d.id)).map((d) => d.name));
        const allowedIds = new Set();
        deptNames.forEach((name) => (deptShiftIds.get(name) || []).forEach((id) => allowedIds.add(id)));
        return activeShifts.filter((s) => allowedIds.has(s.id));
    }, [selectedDepts, availableDepts, deptShiftIds, activeShifts]);

    useEffect(() => {
        if (brush !== OFF_BRUSH && !brushShifts.some((s) => s.id === brush)) {
            setBrush(brushShifts.length ? brushShifts[0].id : OFF_BRUSH);
        }
    }, [brush, brushShifts]);

    const rosterMap = useMemo(() => {
        const m = new Map();
        rosterData.forEach((r) => m.set(`${r.employee}|${r.date}`, r));
        return m;
    }, [rosterData]);

    const cellValue = useCallback((empId, dateStr) => {
        const key = `${empId}|${dateStr}`;
        if (Object.prototype.hasOwnProperty.call(draftEdits, key)) return draftEdits[key];
        const rec = rosterMap.get(key);
        return rec ? rec.shift : null;
    }, [draftEdits, rosterMap]);

    const draftCount = Object.keys(draftEdits).length;

    const guardDiscard = useCallback((action) => {
        if (draftCount > 0) {
            if (!window.confirm(`You have ${draftCount} unpublished change${draftCount > 1 ? "s" : ""}. Continuing will discard them. Continue?`)) return;
            setDraftEdits({});
        }
        action();
    }, [draftCount]);

    const applyPaint = useCallback((pairs) => {
        let skipped = 0;
        const updates = {};
        pairs.forEach(([empId, dateStr, val]) => {
            if (val !== null && !isAllowedShift(empId, val)) { skipped += 1; return; }
            updates[`${empId}|${dateStr}`] = val;
        });
        if (Object.keys(updates).length) setDraftEdits((prev) => ({ ...prev, ...updates }));
        if (skipped) toast.warn(`Skipped ${skipped} cell${skipped > 1 ? "s" : ""} — shift not allowed for that department`);
    }, [isAllowedShift]);

    // ── Date range ──
    const days = useMemo(() => {
        const rawN = Math.round((parseISO(toDate) - parseISO(fromDate)) / 86400000) + 1;
        const n = Math.min(31, Math.max(1, isNaN(rawN) ? 7 : rawN));
        const start = parseISO(fromDate);
        const todayStr = toISO(new Date());
        return Array.from({ length: n }, (_, i) => {
            const d = addDays(start, i);
            const dateStr = toISO(d);
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return {
                dateStr, dow: DOW[(d.getDay() + 6) % 7], dayNum: String(d.getDate()).padStart(2, "0"),
                isWeekend, isToday: dateStr === todayStr,
            };
        });
    }, [fromDate, toDate]);

    const shiftRangeBy = (n) => guardDiscard(() => {
        setFromDate(toISO(addDays(parseISO(fromDate), n)));
        setToDate(toISO(addDays(parseISO(toDate), n)));
    });
    const onFromChange = (e) => {
        const from = e.target.value; if (!from) return;
        guardDiscard(() => {
            setFromDate(from);
            if (parseISO(toDate) < parseISO(from)) setToDate(from);
        });
    };
    const onToChange = (e) => {
        const to = e.target.value; if (!to) return;
        guardDiscard(() => {
            const a = parseISO(fromDate);
            const capped = addDays(a, 30);
            const b = parseISO(to);
            setToDate(b < a ? fromDate : (b > capped ? toISO(capped) : to));
        });
    };
    const applyPreset = (startD, len) => guardDiscard(() => {
        setFromDate(toISO(startD));
        setToDate(toISO(addDays(startD, len - 1)));
    });
    const today = new Date();
    const mondayThisWeek = addDays(today, -((today.getDay() + 6) % 7));
    const presets = [
        { label: "This week", startD: mondayThisWeek, len: 7 },
        { label: "Fortnight", startD: mondayThisWeek, len: 14 },
        {
            label: "This month",
            startD: new Date(today.getFullYear(), today.getMonth(), 1),
            len: new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate(),
        },
    ];

    const endDate = days.length ? parseISO(days[days.length - 1].dateStr) : parseISO(fromDate);
    const startDateObj = parseISO(fromDate);
    const rangeLabel = `${startDateObj.getDate()} ${MON[startDateObj.getMonth()]} – ${endDate.getDate()} ${MON[endDate.getMonth()]} ${endDate.getFullYear()}`;
    const rangeSummary = `${days.length} day${days.length === 1 ? "" : "s"} · ${rangeLabel}`;

    // ── Selection & bulk actions ──
    const toggleEmployee = (id) => setSelectedEmployees((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const toggleAll = () => setSelectedEmployees((prev) => (prev.length === filteredEmployees.length ? [] : filteredEmployees.map((e) => e.id)));

    const applyBrushToSelection = () => applyPaint(selectedEmployees.flatMap((empId) => days.map((d) => [empId, d.dateStr, brushValue])));
    const applyWeekdays = () => applyPaint(selectedEmployees.flatMap((empId) => days.map((d) => [empId, d.dateStr, d.isWeekend ? null : brushValue])));
    const applyRotation = () => {
        if (!activeShifts.length) return;
        applyPaint(selectedEmployees.flatMap((empId) => days.map((d, i) => [empId, d.dateStr, activeShifts[i % activeShifts.length].id])));
    };
    const applyCopyPrevRange = () => {
        const n = days.length;
        applyPaint(selectedEmployees.flatMap((empId) => days.map((d) => {
            const prevDateStr = toISO(addDays(parseISO(d.dateStr), -n));
            return [empId, d.dateStr, cellValue(empId, prevDateStr)];
        })));
    };
    const copyPattern = () => {
        const sourceId = selectedEmployees[0];
        if (!sourceId) return;
        const emp = employeesById.get(sourceId);
        const values = {};
        days.forEach((d) => { values[d.dateStr] = cellValue(sourceId, d.dateStr); });
        setClipboard({ sourceName: emp?.name || sourceId, values });
    };
    const pastePattern = () => {
        if (!clipboard) return;
        applyPaint(selectedEmployees.flatMap((empId) => days.map((d) => [empId, d.dateStr, clipboard.values[d.dateStr] ?? null])));
    };
    const clearSelection = () => applyPaint(selectedEmployees.flatMap((empId) => days.map((d) => [empId, d.dateStr, null])));

    const fillColumn = (dateStr) => applyPaint(filteredEmployees.map((e) => [e.id, dateStr, brushValue]));

    // ── Derived summary/coverage/warnings ──
    const derived = useMemo(() => {
        const n = days.length || 1;
        const perDayCounts = days.map(() => 0);
        let totalShifts = 0;
        const employeeHours = [];
        filteredEmployees.forEach((emp) => {
            let hours = 0;
            days.forEach((d, i) => {
                const shiftId = cellValue(emp.id, d.dateStr);
                if (shiftId !== null) {
                    totalShifts += 1;
                    perDayCounts[i] += 1;
                    const shift = shifts.find((s) => s.id === shiftId);
                    hours += shiftHours(shift);
                }
            });
            employeeHours.push({ emp, hours });
        });
        const limit = n < 7 ? n * 12 : Math.round((48 * n) / 7);
        const overloaded = employeeHours.filter((x) => x.hours > limit);
        const staffCount = filteredEmployees.length;
        const target = Math.max(1, Math.ceil(staffCount * 0.6));
        const coverage = days.map((d, i) => ({
            dateStr: d.dateStr, isWeekend: d.isWeekend, count: perDayCounts[i],
            pct: staffCount ? Math.round((perDayCounts[i] / staffCount) * 100) : 0,
            low: perDayCounts[i] < target,
        }));
        const offDays = staffCount * n - totalShifts;
        return { totalShifts, offDays, staffCount, overloaded, coverage, limit };
    }, [filteredEmployees, days, cellValue, shifts]);

    const warnings = [];
    if (derived.overloaded.length === 1) {
        warnings.push({ text: `${derived.overloaded[0].emp.name} is scheduled over ${derived.limit}h in this range.`, color: "#ff8ba6" });
    } else if (derived.overloaded.length > 1) {
        warnings.push({
            text: `${derived.overloaded.length} staff scheduled over ${derived.limit}h — ${derived.overloaded.slice(0, 3).map((x) => x.emp.name).join(", ")}${derived.overloaded.length > 3 ? ` +${derived.overloaded.length - 3} more` : ""}.`,
            color: "#ff8ba6",
        });
    }
    const uncoveredDays = derived.coverage.filter((c) => c.low).length;
    if (uncoveredDays) warnings.push({ text: `${uncoveredDays} day${uncoveredDays > 1 ? "s" : ""} below the 60% coverage target.`, color: "var(--warning)" });
    if (!warnings.length) warnings.push({ text: "No conflicts. Every day meets the coverage target and nobody is over hours.", color: "var(--success)" });
    const allClear = warnings.length === 1 && warnings[0].color === "var(--success)";

    // ── Publish ──
    const publishDraft = async () => {
        const entries = Object.entries(draftEdits)
            .map(([key, shift_id]) => {
                const idx = key.lastIndexOf("|");
                return { employee_id: key.slice(0, idx), date: key.slice(idx + 1), shift_id };
            })
            .filter(({ employee_id, date, shift_id }) => {
                const rec = rosterMap.get(`${employee_id}|${date}`);
                const serverVal = rec ? rec.shift : null;
                return serverVal !== shift_id;
            });
        if (!entries.length) { setDraftEdits({}); return; }
        setIsPublishing(true);
        try {
            await api.post("roster/assign/", entries);
            setDraftEdits((prev) => {
                const next = { ...prev };
                entries.forEach((e) => {
                    const k = `${e.employee_id}|${e.date}`;
                    if (next[k] === e.shift_id) delete next[k];
                });
                return next;
            });
            await fetchRosterData();
            toast.success(`Published ${entries.length} change${entries.length > 1 ? "s" : ""}`);
        } catch (err) {
            toast.error("Failed to publish — your changes are kept locally, please retry.");
        } finally {
            setIsPublishing(false);
        }
    };

    // ── Export / Import ──
    const getExportUrl = () => {
        let url = `${HRbaseurl}roster/export-xlsx/?from_date=${fromDate}&to_date=${toDate}`;
        if (!selectedDepts.includes("All") && selectedDepts.length > 0) url += `&department=${encodeURIComponent(selectedDepts.join(","))}`;
        return url;
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("from_date", fromDate);
        fd.append("to_date", toDate);
        setIsUploading(true);
        try {
            const res = await api.post("roster/preview-xlsx/", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreviewData(res.data);
            setShowImportModal(true);
        } catch (err) {
            toast.error("Failed to preview: " + (err.response?.data?.error || err.message));
        } finally {
            setIsUploading(false);
            e.target.value = "";
        }
    };

    const handleApplyImport = (acceptedRows) => {
        guardDiscard(async () => {
            setIsUploading(true);
            try {
                const res = await api.post("roster/approve-data/", { preview: acceptedRows });
                toast.success(res.data.message || "Roster imported");
                setShowImportModal(false);
                setPreviewData(null);
                fetchRosterData();
            } catch (err) {
                toast.error("Failed to import: " + (err.response?.data?.error || err.message));
            } finally {
                setIsUploading(false);
            }
        });
    };

    const allEmpSelected = filteredEmployees.length > 0 && filteredEmployees.every((e) => selectedEmployees.includes(e.id));
    const hasSelection = selectedEmployees.length > 0;
    const offColor = shiftColorMap.Off;

    return (
        <Wrap>
            <ToastContainer position="top-right" theme="dark" />

            <ContainerCard>
                <CardHeaderBar>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <SearchWrap>
                            <SearchInput placeholder="Search employee..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </SearchWrap>
                        {!isRestricted && (
                            <DeptSelect value={selectedDepts[0] || "All"} onChange={(e) => { setSelectedDepts([e.target.value]); setSelectedEmployees([]); }}>
                                {uniqueDepartments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </DeptSelect>
                        )}
                        <SavedPill $dirty={draftCount > 0}>
                            <Dot $dirty={draftCount > 0} />
                            {draftCount > 0 ? `${draftCount} unsaved change${draftCount > 1 ? "s" : ""}` : "All changes saved"}
                        </SavedPill>
                    </div>
                    <TopActions>
                        <GhostBtn as="a" href={getExportUrl()} target="_blank" rel="noreferrer" download>
                            <Download size={14} /> Export .xls
                        </GhostBtn>
                        <div style={{ position: "relative" }}>
                            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload}
                                style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", zIndex: 1 }} />
                            <GhostBtn as="span"><Upload size={14} /> {isUploading ? "Reading…" : "Upload"}</GhostBtn>
                        </div>
                        <PrimaryBtn onClick={publishDraft} disabled={draftCount === 0 || isPublishing}>
                            {isPublishing ? "Publishing…" : "Publish roster"}
                        </PrimaryBtn>
                    </TopActions>
                </CardHeaderBar>
                <FilterBar>
                    <RangeGroup>
                        <RangeArrow onClick={() => shiftRangeBy(-days.length)} title="Shift range back">&#8249;</RangeArrow>
                        <DateField>
                            <DateFieldLabel>From</DateFieldLabel>
                            <DateInput type="date" value={fromDate} onChange={onFromChange} />
                        </DateField>
                        <span style={{ color: "var(--text-dim)", fontSize: 13, paddingTop: 12 }}>&rarr;</span>
                        <DateField>
                            <DateFieldLabel>To</DateFieldLabel>
                            <DateInput type="date" value={toDate} min={fromDate} onChange={onToChange} />
                        </DateField>
                        <RangeArrow onClick={() => shiftRangeBy(days.length)} title="Shift range forward">&#8250;</RangeArrow>
                    </RangeGroup>
                    <PresetGroup>
                        {presets.map((p) => {
                            const active = fromDate === toISO(p.startD) && days.length === p.len;
                            return <PresetBtn key={p.label} $active={active} onClick={() => applyPreset(p.startD, p.len)}>{p.label}</PresetBtn>;
                        })}
                    </PresetGroup>
                    <RangeSummary>{rangeSummary}</RangeSummary>
                </FilterBar>

                <ControlBar>
                    <BrushSelectWrap>
                        <BrushLabel>Shift Brush:</BrushLabel>
                        <BrushSelect
                            value={brush === null ? OFF_BRUSH : brush}
                            onChange={(e) => {
                                const val = e.target.value;
                                setBrush(val === OFF_BRUSH ? OFF_BRUSH : Number(val));
                            }}
                            $color={brush === OFF_BRUSH ? offColor : (shiftColorMap[brush] || offColor)}
                        >
                            <option value={OFF_BRUSH}>UA (Unassigned / Off)</option>
                            {brushShifts.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name} ({s.start_time?.slice(0, 5)}–{s.end_time?.slice(0, 5)})
                                </option>
                            ))}
                        </BrushSelect>
                        <BrushBadge $color={brush === OFF_BRUSH ? offColor : (shiftColorMap[brush] || offColor)}>
                            {brush === OFF_BRUSH ? "UA" : (shifts.find((s) => s.id === brush)?.name || "UA")}
                        </BrushBadge>
                    </BrushSelectWrap>

                    <ClipChip $show={!!clipboard} title="Copied pattern">
                        <Copy size={13} /> {clipboard ? `Copied: ${clipboard.sourceName}` : ""}
                    </ClipChip>

                    <div style={{ flex: 1 }} />

                    <TabGroup>
                        <TabBtn $active={activeTab === "view"} onClick={() => setActiveTab("view")}>
                            📊 Summary & View
                        </TabBtn>
                        <TabBtn $active={activeTab === "bulk"} onClick={() => setActiveTab("bulk")}>
                            ⚡ Bulk Actions {hasSelection ? `(${selectedEmployees.length})` : ""}
                        </TabBtn>
                    </TabGroup>
                </ControlBar>

                {activeTab === "view" && (
                    <SummaryStrip>
                        <div style={{ display: "flex", gap: "20px", alignItems: "center", flexWrap: "wrap" }}>
                            <StatItem><SummaryDot $color="var(--primary)" />Shifts assigned: <span>{derived.totalShifts}</span></StatItem>
                            <StatItem><SummaryDot $color="var(--text-dim)" />UA days: <span>{derived.offDays}</span></StatItem>
                            <StatItem><SummaryDot $color="var(--success)" />Staff rostered: <span>{derived.staffCount}</span></StatItem>
                            <StatItem><SummaryDot $color={derived.overloaded.length ? "var(--danger)" : "var(--text-dim)"} />Over {derived.limit}h: <span style={{ color: derived.overloaded.length ? "#ff8ba6" : undefined }}>{derived.overloaded.length}</span></StatItem>
                        </div>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            {allClear ? (
                                <StatusPill $clear={true}>✅ All clear</StatusPill>
                            ) : (
                                <StatusPill $clear={false} title={warnings.map(w => w.text).join('\n')}>⚠️ {warnings.length} Issue{warnings.length > 1 ? 's' : ''}</StatusPill>
                            )}
                        </div>
                    </SummaryStrip>
                )}

                {activeTab === "bulk" && (
                    <BulkTabBar>
                        <BulkBadge>
                            👤 {hasSelection ? `${selectedEmployees.length} Employee(s) Selected` : "Bulk Actions Mode"}
                        </BulkBadge>
                        {hasSelection ? (
                            <BulkTabActionsGroup>
                                <BulkActionButton onClick={applyBrushToSelection}>
                                    Apply {brush === OFF_BRUSH ? "UA" : (shifts.find((s) => s.id === brush)?.name || "")} ({days.length}d)
                                </BulkActionButton>
                                <BulkActionButton onClick={applyWeekdays}>
                                    Weekdays Brush, Weekend UA
                                </BulkActionButton>
                                <BulkActionButton onClick={applyRotation} disabled={!activeShifts.length}>
                                    Rotate Shifts
                                </BulkActionButton>
                                <BulkActionButton onClick={applyCopyPrevRange}>
                                    Copy Prev Pattern
                                </BulkActionButton>
                                <BulkActionButton onClick={copyPattern}>
                                    <Copy size={12} style={{ marginRight: 4 }} />Copy
                                </BulkActionButton>
                                <BulkActionButton onClick={pastePattern} disabled={!clipboard}>
                                    <ClipboardPaste size={12} style={{ marginRight: 4 }} />Paste
                                </BulkActionButton>
                                <BulkDangerButton onClick={clearSelection}>
                                    Clear Shifts
                                </BulkDangerButton>
                                <BulkCloseButton onClick={() => setSelectedEmployees([])} title="Deselect all">
                                    <X size={14} />
                                </BulkCloseButton>
                            </BulkTabActionsGroup>
                        ) : (
                            <BulkTabHint>
                                💡 Tip: Click the checkbox next to employee names in the roster grid below to apply bulk patterns across their schedules.
                            </BulkTabHint>
                        )}
                    </BulkTabBar>
                )}

                <GridCard>
                    <GridScroll>
                        <GridBody style={{ gridTemplateColumns: `220px repeat(${days.length}, minmax(${days.length > 14 ? 46 : (days.length > 7 ? 62 : 90)}px, 1fr))` }}>
                            <HeaderNameCell>
                                <input type="checkbox" checked={allEmpSelected} onChange={toggleAll}
                                    style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: "pointer" }} />
                                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-muted)" }}>Employee</span>
                            </HeaderNameCell>
                            {days.map((d) => (
                                <HeaderDayCell key={d.dateStr} $weekend={d.isWeekend} onClick={() => fillColumn(d.dateStr)}
                                    title={`Fill ${d.dateStr} with the current brush`}>
                                    <DowLabel $weekend={d.isWeekend}>{d.dow}</DowLabel>
                                    <DayNum $weekend={d.isWeekend}>{d.dayNum}</DayNum>
                                </HeaderDayCell>
                            ))}

                            {filteredEmployees.map((emp) => {
                                const empHours = derived.overloaded.find((x) => x.emp.id === emp.id);
                                const hoursVal = (() => {
                                    let h = 0;
                                    days.forEach((d) => {
                                        const sid = cellValue(emp.id, d.dateStr);
                                        if (sid !== null) h += shiftHours(shifts.find((s) => s.id === sid));
                                    });
                                    return h;
                                })();
                                return (
                                    <React.Fragment key={emp.id}>
                                        <NameCell $selected={selectedEmployees.includes(emp.id)}>
                                            <input type="checkbox" checked={selectedEmployees.includes(emp.id)} onChange={() => toggleEmployee(emp.id)}
                                                style={{ width: 15, height: 15, accentColor: "var(--primary)", cursor: "pointer", flex: "none" }} />
                                            <NameInfo>
                                                <NameText>{emp.name}</NameText>
                                                <DeptText>{emp.department}</DeptText>
                                            </NameInfo>
                                            <HoursPill $over={!!empHours} title="Scheduled hours in this range">{hoursVal}h</HoursPill>
                                        </NameCell>
                                        {days.map((d) => {
                                            const shiftId = cellValue(emp.id, d.dateStr);
                                            const shift = shiftId != null ? shifts.find((s) => s.id === shiftId) : null;
                                            const color = shiftId != null ? (shiftColorMap[shiftId] || offColor) : offColor;
                                            const disallowed = brushValue !== null && !isAllowedShift(emp.id, brushValue);
                                            return (
                                                <DayCell
                                                    key={d.dateStr}
                                                    $weekend={d.isWeekend}
                                                    $disallowed={disallowed}
                                                    onMouseDown={() => { paintingRef.current = true; applyPaint([[emp.id, d.dateStr, brushValue]]); }}
                                                    onMouseEnter={() => { if (paintingRef.current) applyPaint([[emp.id, d.dateStr, brushValue]]); }}
                                                    title={`${emp.name} · ${d.dow} ${d.dayNum}${shift ? ` — ${shift.name}` : ""}`}
                                                >
                                                    <CellLabel $color={color}>{shift ? shift.name : "UA"}</CellLabel>
                                                </DayCell>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                            <FooterNameCell>Coverage</FooterNameCell>
                            {derived.coverage.map((cv) => (
                                <CoverageCell key={cv.dateStr}>
                                    <CoverageCount $low={cv.low}>{cv.count}</CoverageCount>
                                    <CoverageBarBg><CoverageBarFill $low={cv.low} style={{ width: `${Math.min(100, cv.pct)}%` }} /></CoverageBarBg>
                                </CoverageCell>
                            ))}
                        </GridBody>
                    </GridScroll>
                </GridCard>

                <HintRow>
                    <span>Click a cell to paint the selected shift · drag across cells to fill a run</span>
                    <span>Click a date header to fill the whole column</span>
                </HintRow>
            </ContainerCard>

            {showImportModal && previewData && (
                <ImportReviewModal
                    data={previewData}
                    shifts={shifts}
                    departments={departments}
                    cellValue={cellValue}
                    isUploading={isUploading}
                    onClose={() => { setShowImportModal(false); setPreviewData(null); }}
                    onApply={handleApplyImport}
                />
            )}
        </Wrap>
    );
};

export default DutyRoster;
